import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { API } from "../utils/apk";

const Dailysale = () => {
  const today = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [month, setMonth] = useState("");
  const [reportData, setReportData] = useState([]);

  const rowColors = [
    "bg-yellow-100",
    "bg-blue-100",
    "bg-green-100",
    "bg-pink-100",
    "bg-purple-100",
    "bg-orange-100",
    "bg-red-100",
    "bg-teal-100",
  ];

  // Fetch report
  const fetchReport = async () => {
    try {
      let queryParam = "";

      if (startDate && endDate) {
        queryParam = `startDate=${startDate}&endDate=${endDate}`;
      } else if (month) queryParam = `month=${month}`;
      else {
        setReportData([]);
        return;
      }

      console.log("API URL:", `${API}/get/daily/monthly/yearly/report?${queryParam}`);

      const response = await fetch(
        `${API}/get/daily/monthly/yearly/report?${queryParam}`
      );

      const result = await response.json();

      console.log("API RESULT:", result);

      if (result.success) {
        setReportData(result.data);
      } else {
        setReportData([]);
      }
    } catch (err) {
      console.error("Error fetching:", err);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [startDate, endDate, month]);

  const handleMonthChange = (e) => {
    setMonth(e.target.value);
    if (e.target.value) {
      setStartDate("");
      setEndDate("");
    }
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    setMonth("");
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
    setMonth("");
  };

  // Grand total
  const grandTotal = reportData.reduce(
    (total, order) =>
      total +
      (order.orderData?.totalPrice ||
        order.orderId?.totalPrice ||
        0),
    0
  );

  // Excel export
  const exportToExcel = () => {
    const excelData = [];

    reportData.forEach((order) => {
      const orderInfo = order.orderData || order.orderId;

      const items =
        order.orderData?.cartItems ||
        order.orderData?.addToCart?.cartIds ||
        order.orderId?.addToCart?.cartIds ||
        [];

      items.forEach((item, index) => {
        excelData.push({
          "Order ID": index === 0 ? orderInfo?.orderId : "",
          "Item Name": item.itemName,
          "Item Rate": item.itemRate,
          Quantity: item.count,
          "Customer Name": index === 0 ? orderInfo?.userAddress?.[0]?.name : "",
          "Customer Mobile": index === 0 ? orderInfo?.userAddress?.[0]?.mobile : "",
          "Delivery Boy": index === 0 ? order.deliveryBoy?.name : "",
          "D.Boy Number": index === 0 ? order.deliveryBoy?.mobileNo : "",
          "Total Price": index === 0 ? orderInfo?.totalPrice : "",
        });
      });
    });

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sale Report");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const fileData = new Blob([excelBuffer], { type: "application/octet-stream" });

    const filename = startDate && endDate
      ? `Sale_Report_${startDate}_to_${endDate}.xlsx`
      : month
      ? `Monthly_Sale_Report_${month}.xlsx`
      : "Sale_Report.xlsx";

    saveAs(fileData, filename);
  };

  return (
    <div className="p-6 bg-blue-200 min-h-screen">
      <h2 className="text-center text-xl font-bold text-white bg-yellow-800 py-2 rounded-t-md px-6">
        SALE REPORT
      </h2>

      {/* Filters */}
      <div className="p-2 flex justify-end items-center gap-4">
        <div className="flex flex-col">
          <label className="text-xs font-semibold text-gray-700">From Date</label>
          <input
            type="date"
            value={startDate}
            max={endDate || undefined}
            onChange={handleStartDateChange}
            className="text-black px-2 py-1 rounded w-36 h-10"
          />
        </div>

        <div className="flex flex-col">
          <label className="text-xs font-semibold text-gray-700">To Date</label>
          <input
            type="date"
            value={endDate}
            min={startDate || undefined}
            max={today}
            onChange={handleEndDateChange}
            className="text-black px-2 py-1 rounded w-36 h-10"
          />
        </div>

        {/* Month Dropdown */}
        <div className="flex flex-col">
          <label className="text-xs font-semibold text-gray-700">Month</label>
          <select
            value={month}
            onChange={handleMonthChange}
            className="text-black px-2 py-1 rounded w-36 h-10"
          >
            <option value="">Select Month</option>
            <option value="january">January</option>
            <option value="february">February</option>
            <option value="march">March</option>
            <option value="april">April</option>
            <option value="may">May</option>
            <option value="june">June</option>
            <option value="july">July</option>
            <option value="august">August</option>
            <option value="september">September</option>
            <option value="october">October</option>
            <option value="november">November</option>
            <option value="december">December</option>
          </select>
        </div>

      </div>

      {/* Table */}
      <div className="overflow-x-auto mt-0 bg-white rounded shadow">
        <div className="max-h-[400px] overflow-y-auto">
          <table className="min-w-full text-sm text-left">
            <thead className="bg-gray-200 font-semibold sticky top-0 z-10">
              <tr>
                {[
                  "Order ID",
                  "Item Name",
                  "Item Rate",
                  "Quantity",
                  "Customer Name",
                  "Customer Mobile",
                  "D.Boy Name",
                  "D.Boy Number",
                  "Total Price",
                ].map((header) => (
                  <th key={header} className="px-4 py-2 bg-gray-200">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {reportData.map((order, idx) => {
                const orderInfo = order.orderData || order.orderId;

                const items =
                  order.orderData?.cartItems ||
                  order.orderData?.addToCart?.cartIds ||
                  order.orderId?.addToCart?.cartIds ||
                  [];

                const rowspan = items.length;

                const rowColor = rowColors[idx % rowColors.length];

                return items.map((item, itemIdx) => (
                  <tr key={idx + "-" + itemIdx} className={`${rowColor} border-b`}>
                    {itemIdx === 0 && (
                      <td rowSpan={rowspan} className="px-4 py-2 font-medium">
                        {orderInfo?.orderId}
                      </td>
                    )}

                    <td className="px-4 py-2">{item.itemName}</td>
<td className="px-4 py-2">
  {Number(item.increasedPrice) > 0
    ? Number(item.increasedPrice).toFixed(2)
    : Number(item.itemRate).toFixed(2)}
</td>
                    <td className="px-4 py-2">{item.count}</td>

                    {itemIdx === 0 && (
                      <>
                        <td rowSpan={rowspan} className="px-4 py-2">
                          {orderInfo?.userAddress?.[0]?.name}
                        </td>

                        <td rowSpan={rowspan} className="px-4 py-2">
                          {orderInfo?.userAddress?.[0]?.mobile}
                        </td>

                        <td rowSpan={rowspan} className="px-4 py-2">
                          {order.deliveryBoy?.name}
                        </td>

                        <td rowSpan={rowspan} className="px-4 py-2">
                          {order.deliveryBoy?.mobileNo}
                        </td>

                        <td
                          rowSpan={rowspan}
                          className="px-4 py-2 font-semibold text-green-600"
                        >
                          ₹{orderInfo?.totalPrice}
                        </td>
                      </>
                    )}
                  </tr>
                ));
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-between items-center mt-4 px-4">
        <div className="font-semibold text-lg">
          Grand Total:
          <span className="font-bold text-green-700"> ₹{grandTotal.toFixed(2)}</span>
        </div>

        <button
          onClick={exportToExcel}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded shadow"
        >
          Export to Excel
        </button>
      </div>
    </div>
  );
};

export default Dailysale;
